/**
 * crm-projections.ts
 *
 * Projection pipeline for CRM state.
 * Secret scrubbing EXCLUSIVELY runs here — never in the reducer or canonical state.
 *
 * Architecture:
 *   CrmState (canonical, verbatim) → projection fn → scrubbed output
 *                                                    ↓
 *                                          AI / RAG / Search index
 *
 * Canonical records are NEVER mutated. Projections are ephemeral derived views.
 */

import type { CrmActivity, Contact, Organization, Deal } from './crm-contracts.js';

// ─── Cross-Namespace RAG Chunk Contract with ACL Metadata ──────────────────────

export interface IndexedCrmChunk {
  chunkId: string;
  documentId: string;
  namespace: string;
  allowedPrincipalIds?: readonly string[];
  allowedRoles?: readonly string[];
  contentHash: string;
  sourceRevision: string;
  text: string;
  containsMaskedSecrets: boolean;
}

// ─── Projection Cache Key & Manager ───────────────────────────────────────────

export interface ProjectionCacheKey {
  entityRevision: string;
  projectionVersion: number;
  redactionPolicyVersion: number;
}

export class ProjectionCacheManager<TEntity extends { id: string; revision: string }, TProjection> {
  private readonly cache = new Map<string, { key: ProjectionCacheKey; projection: TProjection }>();

  getOrCreate(
    entity: TEntity,
    projectionVersion: number,
    redactionPolicyVersion: number,
    factory: (entity: TEntity) => TProjection,
  ): TProjection {
    const cached = this.cache.get(entity.id);
    if (
      cached &&
      cached.key.entityRevision === entity.revision &&
      cached.key.projectionVersion === projectionVersion &&
      cached.key.redactionPolicyVersion === redactionPolicyVersion
    ) {
      return cached.projection;
    }
    const projection = factory(entity);
    this.cache.set(entity.id, {
      key: {
        entityRevision: entity.revision,
        projectionVersion,
        redactionPolicyVersion,
      },
      projection,
    });
    return projection;
  }

  invalidate(entityId: string): void {
    this.cache.delete(entityId);
  }

  clear(): void {
    this.cache.clear();
  }

  has(entityId: string): boolean {
    return this.cache.has(entityId);
  }
}

// ─── Scrubber contract ────────────────────────────────────────────────────────

export interface ScrubResult {
  text: string;
  maskedCount: number;
}

const HIGH_ENTROPY_PATTERNS: RegExp[] = [
  /\bAKIA[A-Z0-9]{16}\b/g,                           // AWS access key
  /\b[A-Za-z0-9+/]{40}\b/g,                           // Generic 40-char base64/hex token
  /\beyJ[A-Za-z0-9-_=]+\.[A-Za-z0-9-_=]+/g,          // JWT header.payload
  /\bsk-[A-Za-z0-9]{32,}\b/g,                          // OpenAI-style secret key
  /\bghp_[A-Za-z0-9]{36}\b/g,                          // GitHub PAT
  /password\s*[:=]\s*\S+/gi,                           // password: <value>
  /api[_-]?key\s*[:=]\s*\S+/gi,                        // api_key: <value>
];

export function defaultScrubber(text: string): ScrubResult {
  let maskedCount = 0;
  let result = text;
  for (const pattern of HIGH_ENTROPY_PATTERNS) {
    result = result.replace(pattern, (match) => {
      maskedCount++;
      return `[REDACTED:${match.slice(0, 6)}…]`;
    });
  }
  return { text: result, maskedCount };
}

// ─── Activity Projections ─────────────────────────────────────────────────────

export interface ActivitySearchProjection {
  activityId: string;
  type: CrmActivity['type'];
  subject: string;
  scrubbedBody: string;
  containsMaskedSecrets: boolean;
  createdBy: string;
  createdAt: number;
  contactId?: string;
  organizationId?: string;
  dealId?: string;
}

export function createActivitySearchProjection(
  activity: CrmActivity,
  scrubberFn: (text: string) => ScrubResult = defaultScrubber,
): ActivitySearchProjection {
  const subjectResult = scrubberFn(activity.subject ?? '');
  const bodyResult = scrubberFn(activity.body);

  return {
    activityId: activity.id,
    type: activity.type,
    subject: subjectResult.text,
    scrubbedBody: bodyResult.text,
    containsMaskedSecrets: subjectResult.maskedCount > 0 || bodyResult.maskedCount > 0,
    createdBy: activity.createdBy,
    createdAt: activity.createdAt,
    contactId: activity.contactId,
    organizationId: activity.organizationId,
    dealId: activity.dealId,
  };
}

export function createSearchProjection(
  activity: Pick<CrmActivity, 'id' | 'subject' | 'body' | 'createdAt'> & { type?: CrmActivity['type']; createdBy?: string },
  scrubberFn: (text: string) => ScrubResult,
): { activityId: string; subject: string; scrubbedBody: string; containsMaskedSecrets: boolean } {
  const subjectResult = scrubberFn(activity.subject ?? '');
  const bodyResult = scrubberFn(activity.body);
  return {
    activityId: activity.id,
    subject: subjectResult.text,
    scrubbedBody: bodyResult.text,
    containsMaskedSecrets: subjectResult.maskedCount > 0 || bodyResult.maskedCount > 0,
  };
}

// ─── RAG Index Chunk Converter with ACL Metadata ──────────────────────────────

export function buildIndexedCrmChunk(
  projection: ActivitySearchProjection,
  namespace: string,
  sourceRevision: string,
  contentHash: string,
  allowedRoles: readonly string[] = ['owner', 'member'],
): IndexedCrmChunk {
  return {
    chunkId: `chunk-${projection.activityId}`,
    documentId: `doc-activity-${projection.activityId}`,
    namespace,
    allowedRoles,
    contentHash,
    sourceRevision,
    text: `${projection.subject}\n${projection.scrubbedBody}`,
    containsMaskedSecrets: projection.containsMaskedSecrets,
  };
}

// ─── Contact display projection ───────────────────────────────────────────────

export interface ContactDisplayProjection {
  id: string;
  displayName: string;
  email?: string;
  phone?: string;
  title?: string;
  organizationId?: string;
  tags: readonly string[];
  isArchived: boolean;
  isMerged: boolean;
}

export function createContactDisplayProjection(contact: Contact): ContactDisplayProjection {
  return {
    id: contact.id,
    displayName: contact.name,
    email: contact.email,
    phone: contact.phone,
    title: contact.title,
    organizationId: contact.organizationId,
    tags: contact.tags,
    isArchived: !!contact.archivedAt,
    isMerged: !!contact.mergedIntoId,
  };
}

// ─── Notes projection ─────────────────────────────────────────────────────────

export interface ContactNotesProjection {
  contactId: string;
  scrubbedNotes: string;
  containsMaskedSecrets: boolean;
}

export function createContactNotesProjection(
  contact: Contact,
  scrubberFn: (text: string) => ScrubResult = defaultScrubber,
): ContactNotesProjection {
  const result = scrubberFn(contact.notes ?? '');
  return {
    contactId: contact.id,
    scrubbedNotes: result.text,
    containsMaskedSecrets: result.maskedCount > 0,
  };
}
